/**
 * ============================================================================
 * DRAFT LEGAL DOCUMENTS — NOT LEGAL ADVICE
 * ============================================================================
 *
 * These are starting drafts written to give the product a complete, coherent
 * shape. They have NOT been reviewed by a lawyer and MUST be reviewed by a
 * qualified Delaware/US (and, given the Australian customer base, Australian;
 * and if selling to UK/EU customers, relevant local) legal adviser before
 * Titrate takes a single paying customer.
 *
 * Operating entity: Clinical CME Group LLC, a Delaware LLC, of 2140 South
 * Dupont Highway, Camden, Delaware, 19934, USA. Contact: Office@clinicalcmegroup.com.
 * The documents are written from that US vantage point — Delaware is the
 * governing law — with carve-outs preserving Australian Consumer Law (and
 * UK/EU) rights for customers those laws protect. They must not describe
 * Titrate as an Australian business.
 *
 * Known gaps the owner must close before launch:
 *   - A lawyer must confirm the Delaware governing-law clause correctly
 *     coexists with mandatory consumer-law rights in Australia, the UK and
 *     the EU for customers there.
 *   - Consumer guarantees under the Australian Consumer Law cannot be excluded;
 *     the refund policy is drafted to sit alongside them, not to override them,
 *     but a lawyer must confirm the wording.
 *   - If any customer is in the UK or EU, UK GDPR / GDPR obligations apply and
 *     the privacy policy needs a lawful-basis analysis and, likely, a data
 *     processing addendum with each sub-processor.
 *   - Sub-processors listed must be kept accurate as the stack changes.
 * ============================================================================
 */
const LAST_UPDATED = 'September 2026';
export const DRAFT_NOTICE = 'This is a draft document provided as a starting point. It has not been reviewed by a lawyer and is not legal advice. It must be reviewed by qualified legal counsel before launch.';
export const TERMS_OF_SERVICE = `# Terms of Service

_Last updated: ${LAST_UPDATED}_

## 1. Who we are

Titrate ("we", "us") is operated by Clinical CME Group LLC, a Delaware limited liability company, of 2140 South Dupont Highway, Camden, Delaware, 19934, USA. You can contact us at Office@clinicalcmegroup.com.

## 2. What Titrate is — and is not

Titrate is an independent study aid for candidates preparing for the Australian Pharmacy Council's CAOP and OPRA registration examinations.

**We are not affiliated with, endorsed by, sponsored by or connected to the Australian Pharmacy Council (APC).** CAOP® and OPRA® are registered trademarks of the APC, used here only to describe the examinations our material is designed to help you prepare for. The APC does not endorse, review or accredit third-party preparation programs, including ours.

All practice questions on Titrate are original content written for this platform. We do not reproduce, paraphrase or reconstruct real examination questions.

## 3. Results are a study signal, not a prediction

The real examinations are reported pass/fail against a scaled psychometric standard. Candidates never receive a raw score, and no third-party result can be mapped to that standard.

Any score, percentage, pace figure or AI-written analysis provided by Titrate is a **practice proxy** to help you find and close knowledge gaps. It is **not** a prediction of your result, and we make no representation that using Titrate will help you pass.

## 4. Educational content only — no clinical advice

Titrate is educational material for examination preparation. It is **not** clinical decision support and must not be relied upon in the care of any real patient. Always consult current, authoritative clinical references and exercise your own professional judgement.

While we take reasonable care, clinical content changes and errors are possible. If you believe a question or explanation is wrong, please tell us at Office@clinicalcmegroup.com.

## 5. Your account

You must be at least 16 years old to create an account. You are responsible for keeping your password secure and for all activity under your account. Tell us promptly if you believe your account has been accessed without your permission.

Provide accurate information when you register, and keep it current.

## 6. Acceptable use

You agree not to:

- share your account with anyone else, or resell access to it;
- copy, scrape, republish or redistribute our questions, explanations, reports or timetables;
- use automated tools to extract content or to generate requests at a volume a human could not;
- attempt to circumvent usage limits, access controls or payment; or
- use Titrate for anything unlawful.

We apply reasonable technical limits to protect the service and its costs. If your use is causing a problem we may contact you, limit your account, or in serious cases suspend it.

## 7. Subscriptions and payment

Paid plans are billed in advance in US dollars through Stripe, our payment processor. We do not store your card details.

You choose your billing period when you subscribe: monthly, or a three-month bundle charged in one payment. Your plan renews automatically for the same period until you cancel. Prices may change; we will give you at least 30 days' notice before a change affects you, and you may cancel before it takes effect.

Plan allowances (feedback reports, revision timetables, tutor messages) are quoted per month, granted in full at the start of each billing period, and do not carry over. A three-month bundle therefore grants three months' worth up front, to be used across those three months.

See our [Refunds and cancellation policy](/refunds) for how to cancel and when refunds are available.

## 8. Intellectual property

We own the Titrate platform and all content on it, including questions, explanations, reports and timetables. Your subscription gives you a personal, non-transferable, non-exclusive licence to use that content for your own examination preparation while your subscription is active.

You own the content you submit. You grant us a licence to store and process it in order to provide the service.

## 9. Availability

We aim to keep Titrate available but do not guarantee uninterrupted service. We may change, suspend or discontinue features. If we discontinue a paid feature materially, we will give reasonable notice and a pro-rata refund where appropriate.

## 10. Liability

Titrate is operated from the United States. Nothing in these terms excludes, restricts or modifies any guarantee, right or remedy you have under a law that cannot lawfully be excluded — including the Australian Consumer Law, if you are in Australia.

Subject to that, to the extent permitted by law our total liability to you in connection with Titrate is limited to the amount you paid us in the 12 months before the claim, and we are not liable for indirect or consequential loss, including examination outcomes or their consequences.

## 11. Termination

You may stop using Titrate and delete your account at any time from your account settings. We may suspend or terminate your account if you materially breach these terms, and we will tell you why unless prevented by law.

## 12. Changes to these terms

We may update these terms. If a change is material we will notify you by email or in the product before it takes effect. Continuing to use Titrate after that means you accept the updated terms.

## 13. Governing law

These terms are governed by the laws of the State of Delaware, USA, without regard to its conflict-of-laws principles, and you and we submit to the non-exclusive jurisdiction of the courts of that state. This does not remove any consumer protection you are entitled to under the law of the country in which you live, including the Australian Consumer Law where it applies to you.
`;
export const PRIVACY_POLICY = `# Privacy Policy

_Last updated: ${LAST_UPDATED}_

## 1. Who is responsible for your data

Clinical CME Group LLC, a Delaware limited liability company, of 2140 South Dupont Highway, Camden, Delaware, 19934, USA, operating as Titrate. Privacy enquiries: Office@clinicalcmegroup.com.

## 2. What we collect

**You give us:**
- Account details — your first and last name, email address, country, and a password (stored only as a bcrypt hash, never in plain text). If you sign in with Google we receive your Google account email and name instead of a password.
- Your marketing preference — whether you have asked us to send study tips and product news, and when you last told us.
- Study profile — which exam you are preparing for, your exam date, your available weekly study hours, whether you are resitting, and how you heard about us. All optional except the exam.
- Your answers, timings and your self-reported reference-book use.
- Anything you type to the AI tutor.

**We generate:**
- Scores, domain breakdowns, pace and reliance figures, AI feedback reports and revision timetables.
- Records of credit-consuming actions, for billing accuracy and abuse prevention.

**We collect automatically:**
- Your browser's time zone, so study reminders and timings are shown in your local time.
- Standard server logs, including IP address, browser type and request times, kept for security and troubleshooting.

**We do not collect:** payment card details. Those go directly to Stripe.

## 3. Google Sign-In data

If you choose "Continue with Google" instead of setting a password, this is what happens with your Google Account data:

- **What we request:** only the \`profile\` and \`email\` scopes — your name and email address. We never request access to your Gmail, Drive, Calendar, Contacts or any other Google service, and we never request a sensitive or restricted scope.
- **What Google sends us:** your name and email address, and confirmation that Google has verified that address.
- **How we use it:** solely to create and authenticate your Titrate account and to address you by name. We do not use it for advertising, and we do not profile you with it beyond the study analytics described in section 2.
- **How long we keep it:** for as long as your Titrate account is open — see "How long we keep it", below.
- **Who we share it with:** nobody, beyond Google itself as part of the sign-in exchange. It is not sold, and it is not given to any of our other sub-processors (see the table below) except where a sub-processor is itself the one providing that data, such as Google during sign-in.
- **Your control over it:** you can disconnect Titrate from your Google Account at any time from your [Google Account's third-party access settings](https://myaccount.google.com/permissions), independently of your Titrate account. Deleting your Titrate account (see "Your rights", below) deletes this data from our systems.

Our use of Google user data across Titrate complies with the Google API Services User Data Policy, including its Limited Use requirements.

## 4. Why we use it

- To provide the service you have asked for — benchmarking, feedback, practice and progress tracking.
- To operate your subscription and enforce plan allowances.
- To keep the service secure and to prevent abuse.
- To improve question quality: we analyse aggregate answer statistics to identify questions that are mis-keyed or ambiguous.
- To email you about your account. We will not send you marketing email without your consent, and you can withdraw it at any time from your account page or any marketing email we send.

## 5. AI processing

When you request a feedback report, a revision timetable or a tutor answer, we send the relevant information to our AI provider (OpenRouter, which routes to the underlying model provider) to generate the response.

What is sent: your performance figures by domain, your exam date and study hours, the question concerned and your answer to it, and your message to the tutor.

What is not sent: your name, your email address or your payment details.

## 6. Who we share it with

We use these sub-processors:

| Provider | Purpose | Data involved |
|---|---|---|
| Railway | Hosting and database | All application data |
| Stripe | Payment processing | Email, name, payment details (held by Stripe) |
| OpenRouter | AI generation | Performance figures, question content, tutor messages |
| Resend | Transactional email | Email address, name |
| Google | Optional sign-in | Google account email and name |

Titrate is operated from the United States, and our sub-processors are located in the United States and other countries. If you are in Australia, the UK or the EU, this means your data is transferred outside your country and processed in the United States and elsewhere. We take reasonable steps to ensure it is handled consistently with this policy.

We do not sell your personal information.

## 7. How long we keep it

We keep your account data while your account is open. If you delete your account, we delete your personal data and all of your study history promptly.

We keep transaction records for as long as tax and accounting law requires, and we may keep anonymised, aggregated statistics (such as how often a given question is answered correctly) that cannot identify you.

## 8. Your rights

You can, at any time:

- **Access and export** everything we hold about you, as JSON, from your account settings.
- **Correct** your details from your account settings.
- **Delete** your account and your data from your account settings.
- **Complain** to us at Office@clinicalcmegroup.com, and if you are not satisfied, to the privacy regulator where you live — for example the Office of the Australian Information Commissioner if you are in Australia, or the Information Commissioner's Office if you are in the UK.

If you are in the UK or EU, you have additional rights under the UK GDPR / GDPR, including the right to object to processing and to data portability. Contact us to exercise them.

## 9. Security

Passwords are hashed with bcrypt. Sessions use signed, httpOnly cookies. Data is encrypted in transit. Access to production data is limited to those who need it.

No system is perfectly secure. If a breach occurs that is likely to cause you serious harm, we will notify you and the relevant regulator as the law requires.

## 10. Cookies

We use a single essential cookie to keep you signed in. We do not use advertising or third-party tracking cookies.

## 11. Changes

We will post any changes here and, if they are material — including any change to how we access, use, store or share Google user data — tell you by email before the change takes effect.
`;
export const REFUND_POLICY = `# Refunds and cancellation

_Last updated: ${LAST_UPDATED}_

## Cancelling

You can cancel at any time from **Account → Manage billing**. Cancellation takes effect at the end of your current billing period — you keep full access until then, and you are not charged again. On a three-month bundle that means access until the end of the three months you have paid for.

We do not require a reason and there is no cancellation fee.

## Refunds

**Within 14 days of your first payment**, we will refund it in full on request, whatever the reason, provided you have not generated more than two AI feedback reports or revision timetables. Email Office@clinicalcmegroup.com.

**After that**, subscription payments are generally not refundable, because your plan's allowances become available to you immediately. You keep access until the end of the period you have paid for.

We will refund a partial period where:

- we are at fault — a prolonged outage, or a charge taken in error;
- you were charged after cancelling; or
- a paid feature is materially discontinued mid-period.

## Your rights under local consumer law

Titrate is operated from the United States. Nothing in this policy limits any right you have under a law that cannot lawfully be excluded — including the Australian Consumer Law, if you are in Australia. If the service is not supplied with due care and skill, or is not fit for its stated purpose, you may be entitled to a remedy regardless of the time limits above.

## Failed payments

If a payment fails we will retry it and email you. If it remains unpaid, your plan reverts to the free tier. Your account, results and history are not deleted.

## How to ask

Email Office@clinicalcmegroup.com from the address on your account. We aim to respond within three business days and to process approved refunds within 10 business days, though your bank may take longer to show it.
`;
export const MARKETPLACE_TERMS = `# Textbook swap — marketplace terms

_Last updated: ${LAST_UPDATED}_

## What this is

The Titrate textbook swap is a noticeboard. It lets candidates advertise reference books to other candidates.

## What we do

We publish your listing once the one-off listing fee is paid, and we show it to other users until you remove it or mark it sold.

## What we do not do

**We are not a party to any sale.** Specifically, we do not:

- handle, hold, escrow or transfer money between buyer and seller;
- take a commission on any sale;
- verify the condition, authenticity, edition or currency of any book;
- arrange, insure or track postage;
- handle customs, duties or import requirements; or
- mediate disputes between buyer and seller.

Buyer and seller deal directly with each other and arrange payment and delivery between themselves, in the same way as any classifieds noticeboard. Any contract of sale is between you and the other party, not with us.

## The listing fee

The listing fee is charged for publishing the advertisement, not for a sale. It is payable whether or not the book sells, and it is not refundable once the listing is published — except where we are at fault or the listing was never published.

## Your responsibilities as a seller

- Describe the book accurately, including its edition and condition. Reference works go out of date; say which edition and year you are selling.
- Only list books you own and are legally entitled to sell.
- Do not list photocopies, scans or other reproductions of copyrighted material.
- Respond to enquiries in good time.

## Your responsibilities as a buyer

- Satisfy yourself about the book's condition and edition before paying.
- Check which edition the examination permits before relying on a second-hand copy.
- Use a payment method that gives you some protection. Sending cash or an irreversible transfer to a stranger carries obvious risk.

## Removing listings

We may remove a listing that appears fraudulent, infringes copyright, or breaches these terms or our Terms of Service. Where we remove a listing through no fault of yours, we will refund the listing fee.

## Liability

To the extent permitted by law, we are not liable for any loss arising from a transaction arranged through the noticeboard, including non-delivery, misdescription, non-payment or the condition of a book. Your rights under any law that cannot lawfully be excluded — including the Australian Consumer Law, if you are in Australia — against us in respect of the listing service itself are unaffected.
`;
/**
 * Page title/description pairs for the legal pages, used both for the SPA's
 * `<title>` (set client-side) and the server-prerendered HTML the API returns
 * for crawlers and bots that never execute the SPA's JavaScript — including
 * Google's OAuth-consent-screen branding verification, which reads the raw
 * HTML response rather than rendering it.
 */
export const LEGAL_PAGE_META = {
    terms: {
        path: '/terms',
        title: 'Terms of Service — Titrate',
        description: 'The terms governing use of Titrate, an independent CAOP and OPRA exam preparation platform not affiliated with the Australian Pharmacy Council.',
        content: TERMS_OF_SERVICE,
    },
    privacy: {
        path: '/privacy',
        title: 'Privacy Policy — Titrate',
        description: 'What personal data Titrate collects from candidates preparing for the CAOP and OPRA exams, why, who it is shared with, and how to access, correct or delete it.',
        content: PRIVACY_POLICY,
    },
    refunds: {
        path: '/refunds',
        title: 'Refunds and Cancellation — Titrate',
        description: 'How to cancel a Titrate subscription and when a refund is available.',
        content: REFUND_POLICY,
    },
    'marketplace-terms': {
        path: '/marketplace-terms',
        title: 'Textbook Swap Terms — Titrate',
        description: 'Terms for the Titrate textbook swap noticeboard, a listing service that is never a party to any sale.',
        content: MARKETPLACE_TERMS,
    },
};
//# sourceMappingURL=legalContent.js.map